import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { FloppyDisk, Buildings, UploadSimple, Trash, Image, PencilSimple } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import FontPicker from "@/components/FontPicker";
import PremiumOptions from "@/components/PremiumOptions";
import { MODULES as DL_MODULES, chooseModuleFolder, clearModuleFolder, loadSavedFolders, isFolderPickSupported } from "@/lib/downloadPaths";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Distinct thumbnail layouts so the user can visually distinguish templates at a glance.
function TemplateThumbnail({ tpl, color }) {
  const c = color;
  const bar = (cls = "") => <div className={`h-0.5 bg-slate-200 ${cls}`} />;
  const layouts = {
    modern: (
      <>
        <div className="flex items-center gap-1 mb-1"><div className="h-3 w-3 rounded-sm" style={{ background: c }} /><div className="h-3 flex-1 rounded-sm" style={{ background: c, opacity: 0.6 }} /></div>
        <div className="h-1 w-2/3 bg-slate-300 mb-2" />
        <div className="h-8 bg-slate-100 mb-1" />
        {bar()}{bar()}{bar("mb-2")}
        <div className="h-2 rounded-sm" style={{ background: c, opacity: 0.6 }} />
      </>
    ),
    "modern-v2": (
      <>
        <div className="flex items-center gap-1 mb-1"><div className="h-2 w-2 rounded-sm" style={{ background: c }} /><div className="h-1 flex-1 bg-slate-300" /><div className="h-2 w-1/3 rounded-sm" style={{ background: c }} /></div>
        <div className="h-px w-full mb-2" style={{ background: c }} />
        <div className="h-7 bg-slate-100 mb-2" />
        {bar()}{bar("mb-2")}
        <div className="h-2 rounded-sm" style={{ background: c }} />
      </>
    ),
    classic: (
      <>
        <div className="h-2 mx-auto w-1/3 rounded-full mb-1" style={{ background: c, opacity: 0.4 }} />
        <div className="h-3 mx-auto w-2/3 mb-1 bg-slate-700" />
        <div className="h-1 mx-auto w-1/2 bg-slate-300 mb-1" />
        <div className="h-2 mx-auto w-1/3 mb-2" style={{ background: c }} />
        <div className="h-px w-full mb-1" style={{ background: c }} />
        <div className="h-7 bg-slate-50 border border-slate-200 mb-1" />
        <div className="h-px w-full mb-1" style={{ background: c }} />
        <div className="h-2 ml-auto w-1/2 bg-slate-700" />
      </>
    ),
    minimal: (
      <>
        <div className="h-1 w-1/3 mb-3 bg-slate-400" />
        <div className="h-5 w-3/4 mb-3" style={{ background: c, opacity: 0.85 }} />
        {bar()}{bar()}{bar("mb-3")}
        <div className="h-7 bg-transparent border-b border-slate-300 mb-2" />
        <div className="h-1 ml-auto w-1/2 bg-slate-400" />
      </>
    ),
    corporate: (
      <>
        <div className="h-5 w-full mb-1" style={{ background: c }} />
        <div className="h-1.5 w-full mb-2" style={{ background: c, opacity: 0.6 }} />
        <div className="grid grid-cols-2 gap-1 mb-1"><div className="h-3 bg-slate-100" /><div className="h-3 bg-slate-100" /></div>
        <div className="h-6 bg-slate-100 mb-1" />
        {bar("mb-2")}
        <div className="h-2 ml-auto w-1/2" style={{ background: c }} />
      </>
    ),
    bold: (
      <>
        <div className="flex items-center gap-1 mb-1"><div className="h-2 w-2 rounded-sm" style={{ background: c }} /><div className="h-2 flex-1 bg-slate-300" /></div>
        <div className="h-6 mb-1" style={{ background: c }} />
        <div className="h-1 w-full mb-2" style={{ background: c }} />
        <div className="h-6 bg-slate-100 mb-1" />
        <div className="h-6 bg-slate-50 mb-2" />
        <div className="h-2 mx-auto w-1/2" style={{ background: c }} />
      </>
    ),
    letterhead: (
      <div className="border-2 border-double rounded-sm p-1 h-full" style={{ borderColor: c }}>
        <div className="h-1 mx-auto w-1/4 mb-1 rounded-full" style={{ background: c, opacity: 0.5 }} />
        <div className="h-2 mx-auto w-2/3 mb-1 bg-slate-700" />
        <div className="h-2 mx-auto w-1/2 mb-2" style={{ background: c, opacity: 0.3 }} />
        <div className="h-5 bg-slate-50 border mb-1" />
        {bar("mb-2")}
        <div className="h-1 mx-auto w-1/3 rounded-full" style={{ background: c }} />
      </div>
    ),
    compact: (
      <>
        <div className="flex items-center gap-1 mb-1"><div className="h-2 w-2 rounded-sm" style={{ background: c }} /><div className="h-1.5 flex-1 bg-slate-300" /><div className="h-2 w-1/3" style={{ background: c }} /></div>
        <div className="h-1.5 w-full mb-1 bg-slate-100" />
        <div className="grid grid-cols-3 gap-px mb-1"><div className="h-1 bg-slate-200" /><div className="h-1 bg-slate-200" /><div className="h-1 bg-slate-200" /></div>
        <div className="h-px w-full" style={{ background: c }} />
        <div className="h-1 bg-slate-100 my-px" /><div className="h-1 bg-slate-100 my-px" /><div className="h-1 bg-slate-100 my-px" /><div className="h-1 bg-slate-100 my-px" />
        <div className="h-1.5 ml-auto w-1/3 mt-1" style={{ background: c }} />
      </>
    ),
    executive: (
      <>
        <div className="grid grid-cols-2 gap-1 mb-1">
          <div className="h-6 border-2 rounded-sm" style={{ borderColor: c }} />
          <div className="h-6 border-2 rounded-sm" style={{ borderColor: c, background: c, opacity: 0.85 }} />
        </div>
        {bar()}{bar()}{bar("mb-2")}
        <div className="h-6 bg-slate-50 border mb-2" />
        <div className="h-2 ml-auto w-1/2" style={{ background: c }} />
      </>
    ),
    receipt: (
      <div className="px-2">
        <div className="h-3 mx-auto w-1/2 mb-1 bg-slate-700" />
        <div className="h-1 mx-auto w-1/3 mb-1 bg-slate-400" />
        <div className="h-px w-full bg-slate-300 mb-1" style={{ borderTop: `1px dashed ${c}` }} />
        <div className="h-1.5 mx-auto w-2/5 mb-1" style={{ background: c }} />
        <div className="h-px w-full bg-slate-300 mb-2" style={{ borderTop: `1px dashed ${c}` }} />
        {bar("mb-px")}{bar("mb-px")}{bar("mb-px")}{bar("mb-2")}
        <div className="h-1.5 mx-auto w-1/2 bg-slate-700" />
      </div>
    ),
    professional: (
      <>
        <div className="flex justify-between items-center mb-1"><div className="h-2 w-1/3 bg-slate-700" /><div className="h-3 w-1/3" style={{ background: c }} /></div>
        <div className="h-px w-full mb-2" style={{ background: c }} />
        <div className="grid grid-cols-2 gap-1 mb-1"><div className="h-3 bg-slate-100 border" /><div className="h-3 bg-slate-100 border" /></div>
        <div className="h-6 bg-slate-100 mb-1" style={{ borderTop: `2px solid ${c}` }} />
        <div className="h-2 ml-auto w-1/2 border-l-4" style={{ borderColor: c, background: "#f1f5f9" }} />
      </>
    ),
    elegant: (
      <>
        <div className="h-1 mx-auto w-1/4 bg-slate-300 mb-1" />
        <div className="h-3 mx-auto w-2/3 mb-1 bg-slate-700 italic" />
        <div className="h-px mx-auto w-1/3 mb-2" style={{ background: c }} />
        <div className="h-7 mb-2" style={{ borderTop: `1px solid ${c}`, borderBottom: `1px solid ${c}` }} />
        {bar()}{bar("mb-2")}
        <div className="h-1.5 ml-auto w-1/3 italic" style={{ background: c, opacity: 0.6 }} />
      </>
    ),
    industrial: (
      <>
        <div className="flex items-center gap-1 mb-px"><div className="h-3 flex-1 bg-slate-800" /><div className="h-3 w-1/3 bg-slate-800 border-2" style={{ borderColor: c }} /></div>
        <div className="h-1 w-full mb-1" style={{ background: c }} />
        <div className="grid grid-cols-3 gap-px mb-1"><div className="h-3 bg-slate-100 border" /><div className="h-3 bg-slate-100 border" /><div className="h-3 bg-slate-100 border" /></div>
        <div className="h-6 mb-1 bg-slate-50" style={{ border: `1.5px solid ${c}` }} />
        <div className="h-2 w-full" style={{ background: c }} />
      </>
    ),
  };
  return <div className="h-[calc(100%-14px)]">{layouts[tpl] || layouts.modern}</div>;
}

const INDIAN_STATES = [
  { name: "Andhra Pradesh", code: "37" },
  { name: "Arunachal Pradesh", code: "12" },
  { name: "Assam", code: "18" },
  { name: "Bihar", code: "10" },
  { name: "Chhattisgarh", code: "22" },
  { name: "Goa", code: "30" },
  { name: "Gujarat", code: "24" },
  { name: "Haryana", code: "06" },
  { name: "Himachal Pradesh", code: "02" },
  { name: "Jharkhand", code: "20" },
  { name: "Karnataka", code: "29" },
  { name: "Kerala", code: "32" },
  { name: "Madhya Pradesh", code: "23" },
  { name: "Maharashtra", code: "27" },
  { name: "Manipur", code: "14" },
  { name: "Meghalaya", code: "17" },
  { name: "Mizoram", code: "15" },
  { name: "Nagaland", code: "13" },
  { name: "Odisha", code: "21" },
  { name: "Punjab", code: "03" },
  { name: "Rajasthan", code: "08" },
  { name: "Sikkim", code: "11" },
  { name: "Tamil Nadu", code: "33" },
  { name: "Telangana", code: "36" },
  { name: "Tripura", code: "16" },
  { name: "Uttar Pradesh", code: "09" },
  { name: "Uttarakhand", code: "05" },
  { name: "West Bengal", code: "19" },
  { name: "Delhi", code: "07" },
];

// ==============================================================================
// GSP Portal Credentials Card — tenant-level "API user" for NIC EWB & IRP portal
// (Master GSP subscription is managed by Super Admin. Only per-portal API user
// credentials are stored per company.)
// ==============================================================================
const GspPortalCredsCard = () => {
  const [state, setState] = useState({
    ewb_api_username: "", ewb_api_password_set: false,
    einv_api_username: "", einv_api_password_set: false,
  });
  const [ewbForm, setEwbForm] = useState({ api_username: "", api_password: "" });
  const [einvForm, setEinvForm] = useState({ api_username: "", api_password: "" });
  const [saving, setSaving] = useState("");
  const [msg, setMsg] = useState("");

  const load = async () => {
    try {
      const r = await axios.get(`${API}/settings/gsp-portal-creds`);
      setState(r.data);
      setEwbForm({ api_username: r.data.ewb_api_username || "", api_password: "" });
      setEinvForm({ api_username: r.data.einv_api_username || "", api_password: "" });
    } catch { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const save = async (kind) => {
    setSaving(kind); setMsg("");
    try {
      const form = kind === "ewb" ? ewbForm : einvForm;
      const payload = { api_username: (form.api_username || "").trim() };
      if (form.api_password) payload.api_password = form.api_password;
      await axios.put(`${API}/settings/gsp-portal-creds/${kind}`, payload);
      setMsg(kind === "ewb" ? "EWB portal creds saved ✓" : "E-Invoice portal creds saved ✓");
      setTimeout(() => setMsg(""), 3000);
      load();
    } catch (e) {
      setMsg(e.response?.data?.detail || "Save failed");
    } finally { setSaving(""); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-6 sm:p-8 mt-6" data-testid="gsp-portal-creds-card">
      <div className="mb-4">
        <h2 className="text-lg font-bold text-slate-900">E-Way Bill &amp; E-Invoice — Portal API User</h2>
        <p className="text-sm text-slate-500 mt-1 leading-relaxed">
          This is a one-time setup. Create your &ldquo;GSP API User&rdquo; credentials on the NIC portal and enter them here —
          auto-generation will then start through the platform's <b>master GSP</b>. The password is stored encrypted.
        </p>
        <p className="text-xs text-slate-400 mt-2">
          <b>EWB:</b> ewaybillgst.gov.in &gt; Registration &gt; For GSP &nbsp;·&nbsp;
          <b>E-Invoice:</b> einvoice1.gst.gov.in &gt; API Registration
        </p>
      </div>

      {msg && <div className="mb-3 text-xs font-semibold text-blue-800 bg-blue-50 border border-blue-200 rounded-sm px-3 py-2" data-testid="gsp-creds-msg">{msg}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* EWB */}
        <div className="border border-amber-200 rounded-sm p-4 bg-amber-50/50" data-testid="ewb-portal-creds-block">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-black text-amber-900 uppercase tracking-wide">E-Way Bill Portal</h3>
            <span className={`text-[10px] font-bold px-2 py-1 rounded-sm ${state.ewb_api_password_set ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>
              {state.ewb_api_password_set ? "CONFIGURED" : "NOT SET"}
            </span>
          </div>
          <div className="space-y-3">
            <div>
              <Label>API Username</Label>
              <Input data-testid="ewb-api-username" value={ewbForm.api_username}
                onChange={(e) => setEwbForm({ ...ewbForm, api_username: e.target.value })}
                placeholder="Your NIC EWB API user" />
            </div>
            <div>
              <Label>API Password {state.ewb_api_password_set && <span className="text-green-600 text-xs">• saved (leave blank to keep)</span>}</Label>
              <Input type="password" data-testid="ewb-api-password" value={ewbForm.api_password}
                onChange={(e) => setEwbForm({ ...ewbForm, api_password: e.target.value })}
                placeholder={state.ewb_api_password_set ? "••••••••" : "NIC EWB API password"} />
            </div>
            <Button type="button" onClick={() => save("ewb")} disabled={saving === "ewb"}
              data-testid="save-ewb-portal-creds"
              className="bg-amber-600 hover:bg-amber-700 text-white font-semibold">
              {saving === "ewb" ? "Saving…" : "Save EWB Portal Creds"}
            </Button>
          </div>
        </div>

        {/* E-Invoice */}
        <div className="border border-blue-200 rounded-sm p-4 bg-blue-50/50" data-testid="einvoice-portal-creds-block">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-black text-blue-900 uppercase tracking-wide">E-Invoice IRP Portal</h3>
            <span className={`text-[10px] font-bold px-2 py-1 rounded-sm ${state.einv_api_password_set ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>
              {state.einv_api_password_set ? "CONFIGURED" : "NOT SET"}
            </span>
          </div>
          <div className="space-y-3">
            <div>
              <Label>API Username</Label>
              <Input data-testid="einv-api-username" value={einvForm.api_username}
                onChange={(e) => setEinvForm({ ...einvForm, api_username: e.target.value })}
                placeholder="Your IRP API user" />
            </div>
            <div>
              <Label>API Password {state.einv_api_password_set && <span className="text-green-600 text-xs">• saved (leave blank to keep)</span>}</Label>
              <Input type="password" data-testid="einv-api-password" value={einvForm.api_password}
                onChange={(e) => setEinvForm({ ...einvForm, api_password: e.target.value })}
                placeholder={state.einv_api_password_set ? "••••••••" : "IRP API password"} />
            </div>
            <Button type="button" onClick={() => save("einvoice")} disabled={saving === "einvoice"}
              data-testid="save-einv-portal-creds"
              className="bg-blue-700 hover:bg-blue-800 text-white font-semibold">
              {saving === "einvoice" ? "Saving…" : "Save E-Invoice Portal Creds"}
            </Button>
          </div>
        </div>
      </div>

      <div className="mt-4 text-xs text-slate-500 italic">
        Wallet charge per successful generation: <b>E-Way Bill ₹1.00</b> &middot; <b>E-Invoice ₹1.00</b>
      </div>
    </div>
  );
};

export default function Settings() {
  const { user, checkAuth } = useAuth();
  const [dlFolders, setDlFolders] = useState({});
  const folderPickSupported = isFolderPickSupported();
  useEffect(() => { loadSavedFolders().then(setDlFolders); }, []);
  const [formData, setFormData] = useState({
    business_name: "", address: "", city: "", state: "Maharashtra", pincode: "", gstin: "",
    phone: "", email: "", logo_alignment: "left",
    bank_account_name: "", bank_account_number: "", bank_name: "", bank_ifsc: "", bank_branch: "",
    bank_account_type: "savings",
    invoice_header_text: "", invoice_footer_text: "", invoice_terms: "",
    ewb_enabled: false, ewb_gsp_provider: "cleartax", ewb_gstin: "",
    ewb_gsp_endpoint: "",
    // these three are write-only — once saved they become encrypted server-side
    ewb_username: "", ewb_password: "", ewb_gsp_api_key: "",
    // PDF Title / Template / Theme customisation
    invoice_title_text: "INVOICE",
    invoice_title_image: "",
    invoice_title_image_size: 60,
    invoice_title_image_position: "left",
    invoice_title_alignment: "center",
    invoice_template: "modern",
    invoice_theme: "blue",
    show_payment_status: true,
    show_bank_details: true,
    show_invoice_title: true,
    show_amount_in_words: true,
    company_name_color: "",
    company_name_font_size: "",
    company_name_font_family: "helvetica",
    company_name_font_weight: "bold",
    company_name_alignment: "",
    logo_size: "medium",
    logo_scale_pct: 0,
    invoice_number_format: "",
    invoice_number_start: 1,
    transport_invoice_number_format: "",
    transport_invoice_number_start: 1,
    transport_invoice_template: "modern",
    transport_invoice_theme: "blue",
  });
  const titleImgRef = useRef(null);
  const [ewbHasCreds, setEwbHasCreds] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [logo, setLogo] = useState("");
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);
  const [companyName, setCompanyName] = useState("");
  const [editingCompanyName, setEditingCompanyName] = useState(false);
  const [savingCompanyName, setSavingCompanyName] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const previewCacheKey = useRef("");

  // Refresh the preview iframe whenever the modal is open and template/theme changes.
  useEffect(() => {
    if (!previewOpen) return;
    const key = `${formData.invoice_template}|${formData.invoice_theme}`;
    if (key === previewCacheKey.current && previewUrl) return;
    previewCacheKey.current = key;
    let cancelled = false;
    (async () => {
      setPreviewLoading(true);
      try {
        // Persist the latest template/theme BEFORE generating preview so PDF reflects
        // the actual company logo, header, bank etc. + chosen template.
        const previewPayload = {
          ...formData,
          company_name_font_size: formData.company_name_font_size === "" || formData.company_name_font_size === null
            ? null
            : parseInt(formData.company_name_font_size, 10) || null,
          company_name_color: formData.company_name_color || null,
          company_name_alignment: formData.company_name_alignment || null,
        };
        await axios.put(`${API}/settings`, previewPayload);
        const res = await axios.get(`${API}/invoices/preview-pdf`, {
          params: { template: formData.invoice_template, theme: formData.invoice_theme, show_bank: true },
          responseType: "blob",
        });
        if (cancelled) return;
        const url = URL.createObjectURL(res.data);
        setPreviewUrl((old) => { if (old) URL.revokeObjectURL(old); return url; });
      } catch (err) {
        console.error("Preview failed", err);
        alert("Could not render preview: " + (err.response?.data?.detail || err.message));
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewOpen, formData.invoice_template, formData.invoice_theme]);

  useEffect(() => {
    fetchSettings();
    fetchCompany();
  }, []);

  const fetchCompany = async () => {
    try {
      const res = await axios.get(`${API}/companies/current`);
      setCompanyName(res.data.name || "");
    } catch (err) { console.error(err); }
  };

  const handleRenameCompany = async () => {
    if (!companyName.trim()) return;
    setSavingCompanyName(true);
    try {
      await axios.put(`${API}/companies/${user.company_id}`, { name: companyName.trim() });
      setEditingCompanyName(false);
      checkAuth(); // refresh user data with new company_name
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to rename");
    } finally {
      setSavingCompanyName(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      if (response.data) {
        setFormData({
          business_name: response.data.business_name || "",
          address: response.data.address || "",
          city: response.data.city || "",
          state: response.data.state || "Maharashtra",
          pincode: response.data.pincode || "",
          gstin: response.data.gstin || "",
          phone: response.data.phone || "",
          email: response.data.email || "",
          logo_alignment: response.data.logo_alignment || "left",
          bank_account_name: response.data.bank_account_name || "",
          bank_account_number: response.data.bank_account_number || "",
          bank_name: response.data.bank_name || "",
          bank_ifsc: response.data.bank_ifsc || "",
          bank_branch: response.data.bank_branch || "",
          bank_account_type: response.data.bank_account_type || "savings",
          invoice_header_text: response.data.invoice_header_text || "",
          invoice_footer_text: response.data.invoice_footer_text || "",
          invoice_terms: response.data.invoice_terms || "",
          ewb_enabled: !!response.data.ewb_enabled,
          ewb_gsp_provider: response.data.ewb_gsp_provider || "cleartax",
          ewb_gstin: response.data.ewb_gstin || "",
          ewb_gsp_endpoint: response.data.ewb_gsp_endpoint || "",
          ewb_username: "", ewb_password: "", ewb_gsp_api_key: "",
          invoice_title_text: response.data.invoice_title_text || "INVOICE",
          invoice_title_image: response.data.invoice_title_image || "",
          invoice_title_image_size: response.data.invoice_title_image_size || 60,
          invoice_title_image_position: response.data.invoice_title_image_position || "left",
          invoice_title_alignment: response.data.invoice_title_alignment || "center",
          invoice_template: response.data.invoice_template || "modern",
          invoice_theme: response.data.invoice_theme || "blue",
          show_payment_status: response.data.show_payment_status !== false,
          show_bank_details: response.data.show_bank_details !== false,
          show_invoice_title: response.data.show_invoice_title !== false,
          show_amount_in_words: response.data.show_amount_in_words !== false,
          company_name_color: response.data.company_name_color || "",
          company_name_font_size: response.data.company_name_font_size || "",
          company_name_font_family: response.data.company_name_font_family || "helvetica",
          company_name_font_weight: response.data.company_name_font_weight || "bold",
          company_name_alignment: response.data.company_name_alignment || "",
          logo_size: response.data.logo_size || "medium",
          logo_scale_pct: response.data.logo_scale_pct || 0,
          invoice_number_format: response.data.invoice_number_format || "",
          invoice_number_start: response.data.invoice_number_start ?? 1,
          transport_invoice_number_format: response.data.transport_invoice_number_format || "",
          transport_invoice_number_start: response.data.transport_invoice_number_start ?? 1,
          transport_invoice_template: response.data.transport_invoice_template || "modern",
          transport_invoice_theme: response.data.transport_invoice_theme || "blue",
        });
        setEwbHasCreds(!!response.data.ewb_has_credentials);
        setLogo(response.data.logo || "");
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Coerce numeric/empty fields so the backend Pydantic model accepts them
      const payload = {
        ...formData,
        company_name_font_size: formData.company_name_font_size === "" || formData.company_name_font_size === null
          ? null
          : parseInt(formData.company_name_font_size, 10) || null,
        company_name_color: formData.company_name_color || null,
        company_name_alignment: formData.company_name_alignment || null,
      };
      await axios.put(`${API}/settings`, payload);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error("Error saving settings:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const formDataFile = new FormData();
      formDataFile.append("file", file);
      const res = await axios.post(`${API}/settings/logo`, formDataFile, { headers: { "Content-Type": "multipart/form-data" } });
      setLogo(res.data.logo);
    } catch (err) {
      alert(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  };

  const handleLogoDelete = async () => {
    try {
      await axios.delete(`${API}/settings/logo`);
      setLogo("");
    } catch (err) {
      console.error("Error:", err);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-1/4"></div>
          <div className="h-96 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-8 max-w-3xl" data-testid="settings-page">
      {user?.role === "super_admin" && (
        <button onClick={() => (window.location.href = "/software-creation")} data-testid="settings-software-creation"
          className="w-full text-left rounded-sm p-5 sm:p-6 mb-6 text-white hover:opacity-95 transition-opacity"
          style={{ background: "linear-gradient(135deg,#4C1D95 0%,#6D28D9 60%,#7C3AED 100%)" }}>
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-violet-200 mb-1">Super Admin Only</p>
          <h2 className="text-2xl font-black tracking-tight mb-1">Software Creation</h2>
          <p className="text-violet-100 text-sm">Har tab/sub-tab ka naam, link, visibility · field-level Editable/Locked toggles · admin delegation — sab yahan se control karein. Changes globally apply honge. →</p>
        </button>
      )}
      {/* ────────────────────────────────────────────────────────────────
          SECTION 1 — COMPANY PROFILE
          Logo • Brand name • GSTIN • Address • Brand typography
          ──────────────────────────────────────────────────────────────── */}
      <div className="rounded-sm p-6 sm:p-8 mb-6 text-white" style={{ background: "linear-gradient(135deg,#0F172A 0%,#1E293B 60%,#334155 100%)" }} data-testid="section-company-profile">
        <p className="text-xs font-bold uppercase tracking-[0.3em] text-blue-300 mb-2">Section 1</p>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tighter mb-2">Company Profile</h1>
        <p className="text-slate-300 text-sm max-w-xl">Your brand identity — logo, name, address &amp; the typography that represents your company on every document.</p>
      </div>

      {/* Company Profile */}
      <div className="bg-white border border-slate-200 rounded-sm p-6 sm:p-8 mb-6">
        <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-200">
          <div className="w-12 h-12 bg-slate-700 rounded-sm flex items-center justify-center">
            <Buildings size={24} weight="duotone" className="text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">Company Profile</h2>
            <p className="text-sm text-slate-500">Your company identity</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {editingCompanyName ? (
            <div className="flex-1 flex items-center gap-2">
              <Input
                data-testid="company-name-edit-input"
                autoCap
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className="flex-1"
                autoFocus
              />
              <Button onClick={handleRenameCompany} data-testid="save-company-name-btn" disabled={savingCompanyName} size="sm">
                {savingCompanyName ? "Saving..." : "Save"}
              </Button>
              <Button variant="outline" onClick={() => { setEditingCompanyName(false); fetchCompany(); }} size="sm">
                Cancel
              </Button>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Company Name</p>
                <p data-testid="company-name-display" className="text-lg font-bold text-slate-900">{companyName || "Not set"}</p>
              </div>
              <Button variant="outline" onClick={() => setEditingCompanyName(true)} data-testid="edit-company-name-btn" size="sm" className="flex items-center gap-1">
                <PencilSimple size={16} /> Rename
              </Button>
            </div>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Company Logo */}
        <div className="bg-white border border-slate-200 rounded-sm p-8 mb-6">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-200">
            <div className="w-12 h-12 bg-blue-600 rounded-sm flex items-center justify-center">
              <Image size={24} weight="duotone" className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">Company Logo</h2>
              <p className="text-sm text-slate-500">Appears on invoices and PDF exports</p>
            </div>
          </div>
          <div className="flex items-center gap-6">
            {logo ? (
              <div className="relative group">
                <img src={logo} alt="Company Logo" data-testid="company-logo-preview" className="w-32 h-32 object-contain border border-slate-200 rounded-sm p-2 bg-white" />
                <button type="button" onClick={handleLogoDelete} data-testid="delete-logo-btn" className="absolute -top-2 -right-2 w-7 h-7 bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <Trash size={14} />
                </button>
              </div>
            ) : (
              <div className="w-32 h-32 border-2 border-dashed border-slate-300 rounded-sm flex items-center justify-center">
                <span className="text-xs text-slate-400 text-center">No logo<br/>uploaded</span>
              </div>
            )}
            <div>
              <input type="file" ref={logoInputRef} accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={handleLogoUpload} className="hidden" />
              <Button type="button" variant="outline" onClick={() => logoInputRef.current?.click()} data-testid="upload-logo-btn" disabled={uploadingLogo} className="flex items-center gap-2">
                <UploadSimple size={18} /> {uploadingLogo ? "Uploading..." : "Upload Logo"}
              </Button>
              <p className="text-xs text-slate-400 mt-2">PNG, JPEG, WebP, SVG. Max 2MB.</p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-8">
          <div className="flex items-center gap-3 mb-8 pb-6 border-b border-slate-200">
            <div className="w-12 h-12 bg-slate-900 rounded-sm flex items-center justify-center">
              <Buildings size={24} weight="duotone" className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">
                Business Information
              </h2>
              <p className="text-sm text-slate-500">
                These details will appear on your invoices
              </p>
            </div>
          </div>

          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="business_name">Business Name *</Label>
                <Input
                  id="business_name"
                  autoCap
                  data-testid="settings-business-name"
                  value={formData.business_name}
                  onChange={(e) =>
                    setFormData({ ...formData, business_name: e.target.value })
                  }
                  placeholder="Enter your business name"
                  required
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="gstin">GSTIN *</Label>
                <Input
                  id="gstin"
                  data-testid="settings-gstin"
                  value={formData.gstin}
                  onChange={(e) =>
                    setFormData({ ...formData, gstin: e.target.value })
                  }
                  placeholder="e.g. 27AABCU9603R1ZM"
                  required
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="address">Address *</Label>
                <Input
                  id="address"
                  data-testid="settings-address"
                  value={formData.address}
                  onChange={(e) =>
                    setFormData({ ...formData, address: e.target.value })
                  }
                  placeholder="Street address"
                  required
                />
              </div>

              <div>
                <Label htmlFor="city">City *</Label>
                <Input
                  id="city"
                  autoCap
                  data-testid="settings-city"
                  value={formData.city}
                  onChange={(e) =>
                    setFormData({ ...formData, city: e.target.value })
                  }
                  required
                />
              </div>

              <div>
                <Label htmlFor="state">State *</Label>
                <select
                  id="state"
                  data-testid="settings-state"
                  value={formData.state}
                  onChange={(e) =>
                    setFormData({ ...formData, state: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  required
                >
                  {INDIAN_STATES.map((state) => (
                    <option key={state.code} value={state.name}>
                      {state.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label htmlFor="pincode">Pincode *</Label>
                <Input
                  id="pincode"
                  data-testid="settings-pincode"
                  value={formData.pincode}
                  onChange={(e) =>
                    setFormData({ ...formData, pincode: e.target.value })
                  }
                  required
                />
              </div>

              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  data-testid="settings-phone"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="settings-email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                />
              </div>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-slate-200 flex items-center gap-4">
            <Button
              type="submit"
              data-testid="save-settings-btn"
              disabled={saving}
              className="bg-slate-900 hover:bg-slate-800 text-white font-semibold flex items-center gap-2"
            >
              <FloppyDisk size={20} weight="bold" />
              {saving ? "Saving..." : "Save Settings"}
            </Button>
            {saved && (
              <span
                data-testid="settings-saved-msg"
                className="text-green-600 font-semibold text-sm"
              >
                Settings saved successfully!
              </span>
            )}
          </div>
        </div>

        {/* Logo & Company Name Customization */}
        <div className="bg-white border border-slate-200 rounded-sm p-6 sm:p-8 mt-6">
          <h2 className="text-lg font-bold text-slate-900 mb-4">Logo &amp; Company Name on PDF</h2>

          {/* Logo */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 pb-6 border-b border-slate-100">
            <div>
              <Label>Logo Size</Label>
              <select
                data-testid="logo-size"
                value={formData.logo_size}
                onChange={(e) => setFormData({ ...formData, logo_size: e.target.value })}
                className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm bg-white"
              >
                <option value="small">Small</option>
                <option value="medium">Medium (default)</option>
                <option value="large">Large</option>
              </select>
              <p className="text-xs text-slate-400 mt-1">Controls how big the logo appears on the PDF.</p>
            </div>
            <div>
              <Label>Logo Alignment</Label>
              <div className="flex gap-2 mt-1">
                {["left", "center", "right"].map((align) => (
                  <label key={align} data-testid={`logo-align-${align}`} className={`flex-1 text-center px-2 py-2 border rounded-sm cursor-pointer text-xs font-semibold capitalize ${formData.logo_alignment === align ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-600"}`}>
                    <input type="radio" name="logo_alignment" value={align} checked={formData.logo_alignment === align} onChange={(e) => setFormData({ ...formData, logo_alignment: e.target.value })} className="hidden" />
                    {align}
                  </label>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-1">Where the logo sits relative to the company name.</p>
            </div>
            <div className="sm:col-span-2">
              <Label>Custom Logo Size (%) — set any size you want</Label>
              <div className="flex items-center gap-3 mt-1">
                <input
                  type="range" min="10" max="400" step="5"
                  data-testid="logo-scale-slider"
                  value={Number(formData.logo_scale_pct) || 100}
                  onChange={(e) => setFormData({ ...formData, logo_scale_pct: parseInt(e.target.value) })}
                  className="flex-1 accent-slate-900 cursor-pointer"
                />
                <Input
                  type="number" min="0" step="1"
                  data-testid="logo-scale-input"
                  value={formData.logo_scale_pct || ""}
                  onChange={(e) => setFormData({ ...formData, logo_scale_pct: e.target.value === "" ? 0 : parseFloat(e.target.value) })}
                  placeholder="e.g. 150"
                  className="w-24"
                />
                <span className="text-sm font-bold text-slate-600">%</span>
                {Number(formData.logo_scale_pct) > 0 && (
                  <button type="button" data-testid="logo-scale-reset" onClick={() => setFormData({ ...formData, logo_scale_pct: 0 })} className="text-xs text-slate-500 hover:text-slate-900 px-2">Reset</button>
                )}
              </div>
              <p className="text-xs text-slate-400 mt-1">
                100% = default size. Type any number (e.g. 30 for very small, 250 for very big) or drag the slider.
                Blank/0 = use the preset above. Applies to invoice AND transport invoice PDFs.
                {Number(formData.logo_scale_pct) > 0 && <b className="text-slate-700"> Current: {formData.logo_scale_pct}%</b>}
              </p>
            </div>
          </div>

          {/* Company Name Typography */}
          <h3 className="text-sm font-bold text-slate-900 mb-3 uppercase tracking-wide">Company Name Typography</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <Label>Text Color</Label>
              <div className="flex gap-2 items-center">
                <input
                  type="color"
                  data-testid="company-name-color"
                  value={formData.company_name_color || "#0F172A"}
                  onChange={(e) => setFormData({ ...formData, company_name_color: e.target.value })}
                  className="h-10 w-14 border border-slate-300 rounded-sm cursor-pointer"
                />
                <Input
                  value={formData.company_name_color}
                  onChange={(e) => setFormData({ ...formData, company_name_color: e.target.value })}
                  placeholder="(theme default)"
                  className="flex-1"
                />
                {formData.company_name_color && (
                  <button type="button" onClick={() => setFormData({ ...formData, company_name_color: "" })} className="text-xs text-slate-500 hover:text-slate-900 px-2">Reset</button>
                )}
              </div>
            </div>

            <div>
              <Label>Font Size (pt)</Label>
              <Input
                type="number"
                min="10"
                max="48"
                data-testid="company-name-size"
                value={formData.company_name_font_size}
                onChange={(e) => setFormData({ ...formData, company_name_font_size: e.target.value })}
                placeholder="(template default)"
              />
              <p className="text-xs text-slate-400 mt-1">Leave blank to use the chosen template's default size.</p>
            </div>

            <div>
              <Label>Font Style</Label>
              <select
                data-testid="company-name-family"
                value={formData.company_name_font_family}
                onChange={(e) => setFormData({ ...formData, company_name_font_family: e.target.value })}
                className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm bg-white"
              >
                <option value="helvetica">Helvetica (sans-serif)</option>
                <option value="times">Times (serif)</option>
                <option value="courier">Courier (monospace)</option>
              </select>
            </div>

            <div>
              <Label>Font Weight</Label>
              <select
                data-testid="company-name-weight"
                value={formData.company_name_font_weight}
                onChange={(e) => setFormData({ ...formData, company_name_font_weight: e.target.value })}
                className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm bg-white"
              >
                <option value="normal">Normal</option>
                <option value="bold">Bold</option>
                <option value="italic">Italic</option>
                <option value="bold-italic">Bold + Italic</option>
              </select>
            </div>

            <div className="sm:col-span-2">
              <Label>Brand Name Font</Label>
              <FontPicker
                testId="brand-name-font-picker"
                value={formData.brand_name_font_key || ""}
                onChange={(k) => setFormData({ ...formData, brand_name_font_key: k })}
                placeholder="(use Font Style above)"
              />
              <p className="text-xs text-slate-400 mt-1">
                Choose any of 60+ stylized fonts or 15 Marathi / Devanagari fonts. Each option is previewed in its own typeface. Overrides the basic Font Style dropdown above on the PDF.
              </p>
            </div>

            <div className="sm:col-span-2">
              <Label>Alignment on PDF</Label>
              <div className="flex gap-2 mt-1">
                {[
                  { v: "", lbl: "Template default" },
                  { v: "left", lbl: "Left" },
                  { v: "center", lbl: "Center" },
                  { v: "right", lbl: "Right" },
                ].map((a) => (
                  <label key={a.v || "default"} data-testid={`company-name-align-${a.v || 'default'}`} className={`flex-1 text-center px-2 py-2 border rounded-sm cursor-pointer text-xs font-semibold ${formData.company_name_alignment === a.v ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-600"}`}>
                    <input type="radio" name="company_name_alignment" value={a.v} checked={formData.company_name_alignment === a.v} onChange={(e) => setFormData({ ...formData, company_name_alignment: e.target.value })} className="hidden" />
                    {a.lbl}
                  </label>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-1">Where the company name appears on the page. "Template default" lets each layout decide.</p>
            </div>
          </div>

          <p className="text-xs text-slate-500 italic mt-4">Click "Live PDF Preview" above to see your changes immediately. Customisations apply to every invoice PDF download going forward.</p>
        </div>

        {/* ────────────────────────────────────────────────────────────────
            SECTION 2 — BILLING & DESIGN
            Invoices • Debit notes • Credit notes • PDF look & feel
            ──────────────────────────────────────────────────────────────── */}
        <div className="rounded-sm p-6 sm:p-8 mt-10 mb-6 text-white" style={{ background: "linear-gradient(135deg,#0C4A6E 0%,#0369A1 60%,#0EA5E9 100%)" }} data-testid="section-billing-design">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-cyan-100 mb-2">Section 2</p>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tighter mb-2">Billing &amp; Design</h1>
          <p className="text-cyan-50 text-sm max-w-xl">Everything that shapes how your invoices, debit notes and credit notes look — templates, theme, body typography, bank details &amp; the text on every printed page.</p>
        </div>

        {/* Download Folder Paths — per-module save-to folder */}
        <div className="bg-white border border-slate-200 rounded-sm p-6 sm:p-8 mt-6" data-testid="section-download-paths">
          <h2 className="text-lg font-bold text-slate-900 mb-1">Download Folder Paths</h2>
          <p className="text-sm text-slate-500 mb-4">
            Choose a folder for each module — every file downloaded from that module will be saved
            straight into its folder automatically. No folder chosen = normal browser download.
          </p>
          {!folderPickSupported && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2 mb-3">
              Your browser doesn't support direct folder saving — use Chrome / Edge on desktop for this feature.
            </p>
          )}
          <div className="divide-y divide-slate-100 border border-slate-200 rounded-sm">
            {DL_MODULES.map((m) => (
              <div key={m.key} className="flex flex-wrap items-center gap-3 px-4 py-2.5" data-testid={`dlpath-row-${m.key}`}>
                <span className="text-sm font-bold text-slate-800 w-44">{m.label}</span>
                <span className="text-xs text-slate-500 flex-1 truncate" data-testid={`dlpath-folder-${m.key}`}>
                  {dlFolders[m.key] ? <b className="text-emerald-700">📁 {dlFolders[m.key]}</b> : <span className="text-slate-400">Not set — browser default</span>}
                </span>
                <button type="button" data-testid={`dlpath-choose-${m.key}`} disabled={!folderPickSupported}
                  onClick={async () => {
                    try {
                      const name = await chooseModuleFolder(m.key);
                      setDlFolders((prev) => ({ ...prev, [m.key]: name }));
                    } catch { /* user cancelled */ }
                  }}
                  className="px-3 py-1.5 text-xs font-bold border border-slate-300 rounded-sm hover:bg-slate-900 hover:text-white transition-colors disabled:opacity-50">
                  Choose Folder
                </button>
                {dlFolders[m.key] && (
                  <button type="button" data-testid={`dlpath-clear-${m.key}`}
                    onClick={async () => { await clearModuleFolder(m.key); setDlFolders((prev) => { const n = { ...prev }; delete n[m.key]; return n; }); }}
                    className="px-2 py-1.5 text-xs font-bold text-rose-600 hover:text-rose-800">
                    Clear
                  </button>
                )}
              </div>
            ))}
          </div>
          <p className="text-[11px] text-slate-400 mt-2">
            Folders are remembered on this device/browser. First download after choosing may ask a one-time permission.
          </p>
        </div>

        {/* Invoice Numbering — sequential, custom-prefix + auto-increment */}
        <div className="bg-white border border-slate-200 rounded-sm p-6 sm:p-8 mt-6" data-testid="section-invoice-numbering">
          <h2 className="text-lg font-bold text-slate-900 mb-1">Invoice Numbering</h2>
          <p className="text-sm text-slate-500 mb-4">
            Pick any custom format with slashes / dashes and a starting number. Every new invoice
            picks the next number automatically. Example: <b>PUNE/ALS/00001</b> → next invoice becomes
            <b> PUNE/ALS/00002</b>.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Custom Format</Label>
              <Input
                data-testid="invoice-number-format"
                value={formData.invoice_number_format}
                onChange={(e) => setFormData({ ...formData, invoice_number_format: e.target.value })}
                placeholder="e.g. PUNE/ALS/00001"
              />
              <p className="text-xs text-slate-400 mt-1">
                The trailing zeros define the width — <b>00001</b> = 5-digit padding. Leave blank to
                use the default <code>INV-00001</code>.
              </p>
            </div>
            <div>
              <Label>Start Numbering From</Label>
              <Input
                type="number"
                min={1}
                data-testid="invoice-number-start"
                value={formData.invoice_number_start}
                onChange={(e) =>
                  setFormData({ ...formData, invoice_number_start: parseInt(e.target.value) || 1 })
                }
                placeholder="1"
              />
              <p className="text-xs text-amber-600 mt-1">
                Changing this <b>resets</b> the counter to this number. Only change if you&apos;re
                starting a fresh sequence.
              </p>
            </div>
          </div>
          <InvoiceNumberPreview
            fmt={formData.invoice_number_format}
            start={formData.invoice_number_start}
          />
        </div>

        {/* Transport Invoice Numbering — dedicated section mirroring the regular
            Invoice Numbering card. Only rendered for users the super_admin has
            entitled with `transport_invoice_enabled`. */}
        {user?.transport_invoice_enabled && (
          <div className="bg-white border border-slate-200 rounded-sm p-6 sm:p-8 mt-6" data-testid="section-transport-invoice-numbering">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-sm bg-sky-100 text-sky-700">Transporter Module</span>
            </div>
            <h2 className="text-lg font-bold text-slate-900 mb-1">Transport Invoice Numbering</h2>
            <p className="text-sm text-slate-500 mb-4">
              Independent register for your Transport (goods carriage) bills. Pick any custom format
              with slashes / dashes and a starting number. Every new transport invoice picks the next
              number automatically. Example: <b>TRP/PUNE/00001</b> → next becomes <b>TRP/PUNE/00002</b>.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Custom Format</Label>
                <Input
                  data-testid="ti-number-format"
                  value={formData.transport_invoice_number_format}
                  onChange={(e) => setFormData({ ...formData, transport_invoice_number_format: e.target.value })}
                  placeholder="e.g. TRP/PUNE/00001"
                />
                <p className="text-xs text-slate-400 mt-1">
                  The trailing zeros define the width — <b>00001</b> = 5-digit padding. Leave blank to
                  use the default <code>TRP-00001</code>.
                </p>
              </div>
              <div>
                <Label>Start Numbering From</Label>
                <Input
                  type="number"
                  min={1}
                  data-testid="ti-number-start"
                  value={formData.transport_invoice_number_start}
                  onChange={(e) => setFormData({ ...formData, transport_invoice_number_start: parseInt(e.target.value) || 1 })}
                  placeholder="1"
                />
                <p className="text-xs text-amber-600 mt-1">
                  Changing this <b>resets</b> the transport-invoice counter to this number. Only change
                  if you&apos;re starting a fresh sequence.
                </p>
              </div>
            </div>
            <InvoiceNumberPreview
              fmt={formData.transport_invoice_number_format || "TRP-00001"}
              start={formData.transport_invoice_number_start}
            />
          </div>
        )}

        {/* Transport Invoice PDF Design — template + colour picker for the 5
            dedicated Transport Invoice designs. Kept as a separate card so
            numbering and visual design don't crowd each other. */}
        {user?.transport_invoice_enabled && (
          <div className="bg-white border border-slate-200 rounded-sm p-6 sm:p-8 mt-6" data-testid="section-transport-invoice-design">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-sm bg-sky-100 text-sky-700">Transporter Module</span>
            </div>
            <h2 className="text-lg font-bold text-slate-900 mb-1">Transport Invoice PDF Design</h2>
            <p className="text-sm text-slate-500 mb-4">
              Choose one of the 5 dedicated Transport Invoice templates and a colour theme. Applies to
              every transport invoice PDF you generate.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Design Template</label>
                <div className="grid grid-cols-5 gap-2">
                  {["modern", "classic", "bold", "minimal", "corporate"].map((t) => {
                    const active = formData.transport_invoice_template === t;
                    return (
                      <button
                        type="button"
                        key={t}
                        data-testid={`ti-template-${t}`}
                        onClick={() => setFormData({ ...formData, transport_invoice_template: t })}
                        className={`px-2 py-3 rounded-sm border text-[11px] font-bold uppercase tracking-wider transition-all ${
                          active
                            ? "border-slate-900 bg-slate-900 text-white"
                            : "border-slate-200 text-slate-600 hover:border-slate-400"
                        }`}
                      >{t}</button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Colour Theme</label>
                <div className="flex items-center gap-3" data-testid="ti-theme-note">
                  <span
                    className="h-9 w-9 rounded-sm border-2 border-slate-900"
                    style={{ background: ({ blue: "#0F4C81", emerald: "#065F46", slate: "#0F172A", maroon: "#7F1D1D" })[formData.invoice_theme] || "#0F4C81" }}
                    title={formData.invoice_theme}
                  />
                  <p className="text-xs text-slate-500">
                    Automatically matches your main bill colour theme (<b className="capitalize text-slate-800">{formData.invoice_theme}</b>).
                    Change it in the <b>Invoice PDF Design</b> section below — transport invoices will use the same colour.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bank Account Details */}
        <div className="bg-white border border-slate-200 rounded-sm p-6 sm:p-8 mt-6">
          <h2 className="text-lg font-bold text-slate-900 mb-4">Bank Account Details</h2>
          <p className="text-sm text-slate-500 mb-4">Optionally shown on printed invoices</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label>Account Holder Name</Label><Input data-testid="bank-account-name" value={formData.bank_account_name} onChange={(e) => setFormData({...formData, bank_account_name: e.target.value})} placeholder="Name on bank account" /></div>
            <div><Label>Account Number</Label><Input data-testid="bank-account-number" value={formData.bank_account_number} onChange={(e) => setFormData({...formData, bank_account_number: e.target.value})} placeholder="Bank account number" /></div>
            <div><Label>Bank Name</Label><Input data-testid="bank-name" value={formData.bank_name} onChange={(e) => setFormData({...formData, bank_name: e.target.value})} placeholder="e.g. State Bank of India" /></div>
            <div><Label>IFSC Code</Label><Input data-testid="bank-ifsc" value={formData.bank_ifsc} onChange={(e) => setFormData({...formData, bank_ifsc: e.target.value})} placeholder="e.g. SBIN0001234" /></div>
            <div><Label>Branch</Label><Input data-testid="bank-branch" value={formData.bank_branch} onChange={(e) => setFormData({...formData, bank_branch: e.target.value})} placeholder="Branch name" /></div>
            <div>
              <Label>Account Type</Label>
              <select data-testid="bank-account-type" value={formData.bank_account_type} onChange={(e) => setFormData({...formData, bank_account_type: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm">
                <option value="savings">Savings</option>
                <option value="current">Current</option>
                <option value="overdraft">Overdraft</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
        </div>

        {/* Invoice Text Customization */}
        <div className="bg-white border border-slate-200 rounded-sm p-6 sm:p-8 mt-6">
          <h2 className="text-lg font-bold text-slate-900 mb-4">Invoice Text Customization</h2>
          <p className="text-sm text-slate-500 mb-4">Custom text shown on your invoices</p>
          <div className="space-y-4">
            <div><Label>Header Text</Label><Input data-testid="invoice-header-text" value={formData.invoice_header_text} onChange={(e) => setFormData({...formData, invoice_header_text: e.target.value})} placeholder="e.g. Tax Invoice / Proforma Invoice" /></div>
            <div><Label>Footer Text</Label><Input data-testid="invoice-footer-text" value={formData.invoice_footer_text} onChange={(e) => setFormData({...formData, invoice_footer_text: e.target.value})} placeholder="e.g. Thank you for your business!" /></div>
            <div><Label>Terms & Conditions</Label><Textarea data-testid="invoice-terms" autoCap value={formData.invoice_terms} onChange={(e) => setFormData({...formData, invoice_terms: e.target.value})} rows={3} placeholder="Payment terms, warranty info, etc." /></div>
          </div>
        </div>

        {/* PDF Invoice Customisation */}
        <div className="bg-white border border-slate-200 rounded-sm p-6 sm:p-8 mt-6" data-testid="pdf-custom-section">
          <h2 className="text-lg font-bold text-slate-900 mb-1">Invoices, Debit &amp; Credit Notes — PDF Look</h2>
          <p className="text-sm text-slate-500 mb-6">Title text, body typography, layout template &amp; colour theme. Applies to every invoice, debit note and credit note you generate.</p>

          {/* Body Font + Title text + alignment */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <div className="sm:col-span-3">
              <Label>Body Font (applies to invoices &amp; notes)</Label>
              <FontPicker
                testId="billing-body-font-picker"
                value={formData.billing_body_font_key || ""}
                onChange={(k) => setFormData({ ...formData, billing_body_font_key: k })}
                placeholder="Default (Helvetica)"
              />
              <p className="text-xs text-slate-400 mt-1">
                Choose from 60+ stylized + 15 Marathi fonts. Applies to invoice, debit-note and credit-note PDFs across all 13 templates.
              </p>
            </div>
            <div className="sm:col-span-2">
              <Label>Invoice Title Text</Label>
              <Input data-testid="invoice-title-text" value={formData.invoice_title_text} onChange={(e) => setFormData({...formData, invoice_title_text: e.target.value})} placeholder="INVOICE / TAX INVOICE / BILL …" />
            </div>
            <div>
              <Label>Title Alignment</Label>
              <select data-testid="title-alignment" value={formData.invoice_title_alignment} onChange={(e) => setFormData({...formData, invoice_title_alignment: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm">
                <option value="left">Left</option><option value="center">Center</option><option value="right">Right</option>
              </select>
            </div>
          </div>

          {/* Title image upload + size + position */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 items-end mb-4">
            <div className="sm:col-span-2">
              <Label>Image beside title (optional)</Label>
              <div className="flex items-center gap-2">
                <input ref={titleImgRef} type="file" accept="image/*" className="hidden" data-testid="title-img-input" onChange={(e) => {
                  const f = e.target.files?.[0]; if (!f) return;
                  if (f.size > 1024*1024) return alert("Max 1 MB");
                  const reader = new FileReader();
                  reader.onload = () => setFormData({...formData, invoice_title_image: reader.result});
                  reader.readAsDataURL(f);
                }} />
                {formData.invoice_title_image ? (
                  <div className="flex items-center gap-2 px-2 py-1 border border-slate-200 rounded-sm w-full">
                    <img src={formData.invoice_title_image} alt="title" style={{ height: 28 }} />
                    <button type="button" onClick={() => setFormData({...formData, invoice_title_image: ""})} data-testid="title-img-clear" className="text-xs text-rose-600 ml-auto"><Trash size={14} /></button>
                  </div>
                ) : (
                  <Button type="button" variant="outline" size="sm" onClick={() => titleImgRef.current?.click()} data-testid="title-img-upload"><UploadSimple size={14} className="mr-1" /> Upload Image</Button>
                )}
              </div>
            </div>
            <div>
              <Label>Image Size ({formData.invoice_title_image_size}px)</Label>
              <input type="range" min="40" max="120" step="2" value={formData.invoice_title_image_size} onChange={(e) => setFormData({...formData, invoice_title_image_size: parseInt(e.target.value)})} data-testid="title-img-size" className="w-full" />
            </div>
            <div>
              <Label>Image Position</Label>
              <select data-testid="title-img-position" value={formData.invoice_title_image_position} onChange={(e) => setFormData({...formData, invoice_title_image_position: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm">
                <option value="left">Left of title</option>
                <option value="right">Right of title</option>
                <option value="above">Above title</option>
                <option value="below">Below title</option>
              </select>
            </div>
          </div>

          {/* Live Preview */}
          <div className="border-2 border-dashed border-slate-300 rounded-sm p-6 bg-slate-50 mb-6" data-testid="title-preview">
            <p className="text-[10px] font-bold uppercase text-slate-400 mb-3">Live Preview</p>
            {(() => {
              const align = formData.invoice_title_alignment;
              const pos = formData.invoice_title_image_position;
              const sz = formData.invoice_title_image_size;
              const themeMap = { blue: { p: "#0F172A", a: "#2563EB" }, emerald: { p: "#064E3B", a: "#059669" }, slate: { p: "#0F172A", a: "#475569" }, maroon: { p: "#450A0A", a: "#B91C1C" } };
              const t = themeMap[formData.invoice_theme] || themeMap.blue;
              const titleFont = { modern: 32, classic: 28, minimal: 36, corporate: 26, bold: 40 }[formData.invoice_template] || 32;
              const titleEl = <span style={{ fontSize: `${titleFont}px`, fontWeight: 900, color: t.p, lineHeight: 1, letterSpacing: "-0.02em" }}>{formData.invoice_title_text || "INVOICE"}</span>;
              const imgEl = formData.invoice_title_image ? <img src={formData.invoice_title_image} alt="title" style={{ height: sz, width: "auto", objectFit: "contain" }} /> : null;
              const stack = pos === "above" || pos === "below";
              const order = pos === "above" ? [imgEl, titleEl] : pos === "below" ? [titleEl, imgEl] : pos === "right" ? [titleEl, imgEl] : [imgEl, titleEl];
              return (
                <div style={{ display: "flex", flexDirection: stack ? "column" : "row", alignItems: "center", justifyContent: align === "left" ? "flex-start" : align === "right" ? "flex-end" : "center", gap: 16 }}>
                  {order.filter(Boolean).map((el, i) => <div key={i}>{el}</div>)}
                </div>
              );
            })()}
            <p className="text-center mt-2 text-xs" style={{ color: ({ blue:"#2563EB",emerald:"#059669",slate:"#475569",maroon:"#B91C1C" })[formData.invoice_theme] }}><b>{formData.business_name || "Your Business"}</b></p>
          </div>

          {/* Templates × Themes picker */}
          <div className="flex items-center justify-between mb-2">
            <Label className="mb-0 block">Choose a Template &amp; Theme</Label>
            <Button
              type="button"
              data-testid="live-preview-btn"
              onClick={() => setPreviewOpen(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold"
              size="sm"
            >
              Live PDF Preview
            </Button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3 mb-2" data-testid="template-grid">
            {[
              { tpl: "modern", lbl: "Modern" },
              { tpl: "modern-v2", lbl: "Modern V2" },
              { tpl: "classic", lbl: "Classic" },
              { tpl: "minimal", lbl: "Minimal" },
              { tpl: "corporate", lbl: "Corporate" },
              { tpl: "bold", lbl: "Bold" },
              { tpl: "letterhead", lbl: "Letterhead" },
              { tpl: "compact", lbl: "Compact" },
              { tpl: "executive", lbl: "Executive" },
              { tpl: "receipt", lbl: "Receipt" },
              { tpl: "professional", lbl: "Professional" },
              { tpl: "elegant", lbl: "Elegant" },
              { tpl: "industrial", lbl: "Industrial" },
            ].flatMap(t => [
              { tpl: t.tpl, theme: "blue", lbl: t.lbl, color: "#2563EB" },
              { tpl: t.tpl, theme: "emerald", lbl: t.lbl, color: "#059669" },
              { tpl: t.tpl, theme: "slate", lbl: t.lbl, color: "#475569" },
              { tpl: t.tpl, theme: "maroon", lbl: t.lbl, color: "#B91C1C" },
            ]).map((combo) => {
              const active = formData.invoice_template === combo.tpl && formData.invoice_theme === combo.theme;
              return (
                <button
                  key={`${combo.tpl}-${combo.theme}`}
                  type="button"
                  onClick={() => setFormData({...formData, invoice_template: combo.tpl, invoice_theme: combo.theme})}
                  data-testid={`tpl-${combo.tpl}-${combo.theme}`}
                  className={`relative aspect-[3/4] rounded-sm border-2 p-2 text-left transition-all ${active ? "border-blue-600 ring-2 ring-blue-300 shadow-md" : "border-slate-200 hover:border-slate-400"}`}
                >
                  <TemplateThumbnail tpl={combo.tpl} color={combo.color} />
                  <p className="absolute bottom-1 left-1 right-1 text-[9px] font-bold uppercase text-slate-700 text-center">{combo.lbl}</p>
                  {active && <span className="absolute top-1 right-1 text-[9px] bg-blue-600 text-white px-1.5 py-0.5 rounded-sm font-bold">SELECTED</span>}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-slate-500 italic mb-6">13 layouts × 4 themes = 52 combinations. Currently selected: <b>{formData.invoice_template}</b> / <b>{formData.invoice_theme}</b>. Click <b>Live PDF Preview</b> to see how a real invoice will look.</p>

          {/* ===================== PREMIUM OPTIONS ===================== */}
          <PremiumOptions
            user={user}
            current={formData.invoice_template}
            onPick={(key) => setFormData({ ...formData, invoice_template: key })}
          />

          {/* PDF behaviour toggles */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-sm">
              <div>
                <p className="font-semibold text-slate-900">Show Invoice Title</p>
                <p className="text-xs text-slate-500">When OFF, the "INVOICE" / custom title banner is completely hidden on the PDF across all templates.</p>
              </div>
              <label className="flex items-center gap-2">
                <input type="checkbox" data-testid="show-title-toggle" checked={formData.show_invoice_title} onChange={(e) => setFormData({...formData, show_invoice_title: e.target.checked})} className="w-5 h-5 accent-blue-600" />
                <span className="text-sm font-semibold">{formData.show_invoice_title ? "Visible" : "Hidden"}</span>
              </label>
            </div>

            <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-sm">
              <div>
                <p className="font-semibold text-slate-900">Show Bank Details on PDF</p>
                <p className="text-xs text-slate-500">When ON, the bank account block (filled below) prints by default on every invoice.</p>
              </div>
              <label className="flex items-center gap-2">
                <input type="checkbox" data-testid="show-bank-details-toggle" checked={formData.show_bank_details} onChange={(e) => setFormData({...formData, show_bank_details: e.target.checked})} className="w-5 h-5 accent-blue-600" />
                <span className="text-sm font-semibold">{formData.show_bank_details ? "Visible" : "Hidden"}</span>
              </label>
            </div>

            <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-sm">
              <div>
                <p className="font-semibold text-slate-900">Show Amount in Words</p>
                <p className="text-xs text-slate-500">Prints the total amount as Indian-English words below the totals row — like on a cheque (e.g. "Rupees Fifty Nine Thousand Only").</p>
              </div>
              <label className="flex items-center gap-2">
                <input type="checkbox" data-testid="show-amount-words-toggle" checked={formData.show_amount_in_words} onChange={(e) => setFormData({...formData, show_amount_in_words: e.target.checked})} className="w-5 h-5 accent-blue-600" />
                <span className="text-sm font-semibold">{formData.show_amount_in_words ? "Visible" : "Hidden"}</span>
              </label>
            </div>

            <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-sm">
              <div>
                <p className="font-semibold text-slate-900">Show Payment Status on PDF</p>
                <p className="text-xs text-slate-500">When OFF, the PAID / UNPAID badge is completely hidden. You can also override this per invoice.</p>
              </div>
              <label className="flex items-center gap-2">
                <input type="checkbox" data-testid="show-payment-toggle" checked={formData.show_payment_status} onChange={(e) => setFormData({...formData, show_payment_status: e.target.checked})} className="w-5 h-5 accent-blue-600" />
                <span className="text-sm font-semibold">{formData.show_payment_status ? "Visible" : "Hidden"}</span>
              </label>
            </div>
          </div>
        </div>

        {/* Portal API User Credentials — NIC E-Way Bill + IRP E-Invoice */}
        <GspPortalCredsCard />

        {/* Save All button */}
        <div className="mt-6 flex items-center gap-4">
          <Button type="submit" data-testid="save-all-settings-btn" disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white font-semibold flex items-center gap-2">
            <FloppyDisk size={20} weight="bold" /> {saving ? "Saving..." : "Save All Settings"}
          </Button>
          {saved && <span className="text-green-600 font-semibold text-sm">All settings saved!</span>}
        </div>
      </form>

      {/* Live PDF Preview modal */}
      {previewOpen && (
        <div
          className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center bg-slate-900/70 p-0 sm:p-4"
          data-testid="preview-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setPreviewOpen(false); }}
        >
          <div className="w-full max-w-5xl h-full sm:h-[90vh] bg-white rounded-none sm:rounded-md shadow-2xl flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-slate-50">
              <div>
                <p className="text-base font-bold text-slate-900">Live PDF Preview</p>
                <p className="text-xs text-slate-500">Template: <b className="text-slate-800">{formData.invoice_template}</b> &middot; Theme: <b className="text-slate-800">{formData.invoice_theme}</b> &middot; Uses your real logo, company info, header/footer &amp; bank details with sample line items.</p>
              </div>
              <button
                type="button"
                onClick={() => setPreviewOpen(false)}
                data-testid="preview-close-btn"
                className="text-slate-500 hover:text-slate-900 text-xl leading-none px-2"
                aria-label="Close preview"
              >×</button>
            </div>
            <div className="flex-1 relative bg-slate-100">
              {previewLoading && (
                <div className="absolute inset-0 flex items-center justify-center" data-testid="preview-loading">
                  <div className="text-center">
                    <div className="inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-sm font-semibold text-slate-700 mt-2">Rendering PDF…</p>
                  </div>
                </div>
              )}
              {previewUrl ? (
                <iframe
                  key={previewUrl}
                  title="invoice-preview"
                  src={previewUrl}
                  className="w-full h-full border-0"
                  data-testid="preview-iframe"
                />
              ) : null}
            </div>
            <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between gap-2">
              <p className="text-xs text-slate-500">Click any template above to instantly re-render this preview.</p>
              <div className="flex items-center gap-2">
                {previewUrl && (
                  <a
                    href={previewUrl}
                    download={`invoice-${formData.invoice_template}-${formData.invoice_theme}.pdf`}
                    data-testid="preview-download-btn"
                    className="text-xs font-semibold px-3 py-2 rounded-sm border border-slate-300 hover:bg-slate-100"
                  >Download Sample</a>
                )}
                <Button
                  type="button"
                  onClick={() => setPreviewOpen(false)}
                  className="bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold"
                  size="sm"
                  data-testid="preview-done-btn"
                >Done</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Live preview of what the next 3 invoice numbers will look like. Purely
 * client-side: parses the trailing digits of `fmt` to figure out the
 * prefix + zero-padding width, then formats `start`, `start+1`, `start+2`
 * against that. Matches the backend `_parse_invoice_format` exactly.
 */
function InvoiceNumberPreview({ fmt, start }) {
  const parsed = parseInvoiceFormat(fmt);
  const s = Math.max(1, parseInt(start) || 1);
  const samples = [0, 1, 2].map((i) =>
    `${parsed.prefix}${String(s + i).padStart(parsed.pad, "0")}`
  );
  return (
    <div className="mt-5 bg-slate-50 border border-slate-200 rounded-sm p-4">
      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">
        Next 3 invoice numbers
      </p>
      <div className="flex flex-wrap gap-2">
        {samples.map((s2, i) => (
          <span
            key={i}
            data-testid={`inv-number-sample-${i}`}
            className="font-mono text-sm font-bold px-3 py-1.5 bg-white border border-slate-300 rounded-sm text-slate-800"
          >
            {s2}
          </span>
        ))}
      </div>
    </div>
  );
}

function parseInvoiceFormat(fmt) {
  const trimmed = (fmt || "").trim();
  if (!trimmed) return { prefix: "INV-", pad: 5 };
  const m = trimmed.match(/(\d+)\s*$/);
  if (!m) return { prefix: trimmed, pad: 5 };
  return { prefix: trimmed.slice(0, m.index), pad: m[1].length };
}
